import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { fetchOrders } from '../../../lib/checkout';

const STATUS_FILTERS = [
  { id: 'all',        label: 'All Orders' },
  { id: 'processing', label: 'Processing' },
  { id: 'completed',  label: 'Completed' },
  { id: 'on-hold',    label: 'On hold' },
  { id: 'cancelled',  label: 'Cancelled' },
];

const STATUS_STYLES = {
  completed:  { icon: 'CheckCircle', color: 'text-green-500',  badge: 'bg-green-100 text-green-800' },
  processing: { icon: 'Clock',       color: 'text-amber-500',  badge: 'bg-amber-100 text-amber-800' },
  'on-hold':  { icon: 'Pause',       color: 'text-blue-500',   badge: 'bg-blue-100 text-blue-800' },
  cancelled:  { icon: 'XCircle',     color: 'text-red-500',    badge: 'bg-red-100 text-red-800' },
  refunded:   { icon: 'RotateCcw',   color: 'text-purple-500', badge: 'bg-purple-100 text-purple-800' },
  failed:     { icon: 'AlertCircle', color: 'text-red-500',    badge: 'bg-red-100 text-red-800' },
  pending:    { icon: 'Clock',       color: 'text-gray-500',   badge: 'bg-gray-100 text-gray-800' },
};

const fmt = (n, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n || 0);

const OrderHistory = () => {
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetchOrders()
      .then((rows) => { if (!cancelled) setOrders(rows); })
      .catch(() => { if (!cancelled) setLoadError('Could not load your orders. Refresh to try again.'); });
    return () => { cancelled = true; };
  }, []);

  const loading = orders === null && !loadError;
  const list = orders || [];

  const filtered = list.filter((o) => {
    const okStatus = selectedFilter === 'all' || o.status === selectedFilter;
    const term = searchTerm.trim().toLowerCase();
    const okSearch =
      !term ||
      String(o.order_number || o.id).toLowerCase().includes(term) ||
      (o.items || []).some((it) => (it.name || '').toLowerCase().includes(term));
    return okStatus && okSearch;
  });

  const countFor = (id) => id === 'all' ? list.length : list.filter((o) => o.status === id).length;

  return (
    <div className="max-w-6xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Order History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and track all your orders</p>
            </div>
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="pl-10 pr-4 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFilter(f.id)}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  selectedFilter === f.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {f.label}
                <span className="ml-2 text-xs bg-black/10 px-1.5 py-0.5 rounded-full">{countFor(f.id)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-border">
          {loading && (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading your orders…</div>
          )}
          {loadError && !loading && (
            <div className="p-12 text-center text-sm text-destructive">{loadError}</div>
          )}
          {!loading && !loadError && filtered.length === 0 && (
            <div className="p-12 text-center">
              <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No orders found</h3>
              <p className="text-sm text-muted-foreground mb-6">
                {searchTerm ? 'Try adjusting your search terms' : "You haven't placed any orders yet"}
              </p>
              <Button onClick={() => navigate('/product-catalog')}>Start Shopping</Button>
            </div>
          )}
          {!loading && !loadError && filtered.map((order) => {
            const style = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
            return (
              <div key={order.id} className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-medium text-foreground mb-1">Order #{order.order_number}</h3>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span>
                            Placed on {order.date_created ? new Date(order.date_created).toLocaleDateString() : '—'}
                          </span>
                          <span>•</span>
                          <span className="flex items-center">
                            <Icon name={style.icon} size={14} className={cn('mr-1', style.color)} />
                            <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', style.badge)}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground">{fmt(order.total, order.currency)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{order.item_count} item{order.item_count === 1 ? '' : 's'}</p>
                      </div>
                    </div>

                    {order.items && order.items.length > 0 && (
                      <div className="space-y-3">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex items-center gap-3 text-sm">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-12 h-12 rounded-md object-cover border border-border shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-md bg-muted border border-border shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-foreground truncate">{item.name}</p>
                              {item.attributes && Object.keys(item.attributes).length > 0 && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {Object.entries(item.attributes)
                                    .map(([k, v]) => `${k}: ${v}`)
                                    .join(' · ')}
                                </p>
                              )}
                            </div>
                            <span className="text-muted-foreground ml-3 shrink-0">
                              Qty {item.quantity} · {fmt(item.total, order.currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
